import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAuth = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Password reset states
    const [showResetForm, setShowResetForm] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [securityQuestion, setSecurityQuestion] = useState('');
    const [securityAnswer, setSecurityAnswer] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [resetStep, setResetStep] = useState(1);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const response = await axios.post(`${API}/admin/login`, { email, password });
            const { token, admin } = response.data;
            
            localStorage.setItem('adminToken', token);
            onLogin(token, admin);
            toast.success('Login successful');
        } catch (error) {
            const message = error.response?.data?.detail || 'Invalid email or password';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const fetchSecurityQuestion = async () => {
        if (!resetEmail) {
            toast.error('Please enter your email');
            return;
        }
        
        try {
            const response = await axios.get(`${API}/admin/security-question/${resetEmail}`);
            setSecurityQuestion(response.data.security_question);
            setResetStep(2);
        } catch (error) {
            toast.error('Email not found');
        }
    };

    const handlePasswordReset = async () => {
        if (!securityAnswer || !newPassword) {
            toast.error('Please fill all fields');
            return;
        }
        
        setLoading(true);
        try {
            await axios.post(`${API}/admin/reset-password`, {
                email: resetEmail,
                security_answer: securityAnswer,
                new_password: newPassword
            });
            toast.success('Password reset successful! Please login with your new password.');
            setShowResetForm(false);
            setResetStep(1);
            setResetEmail('');
            setSecurityAnswer('');
            setNewPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Password reset failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-secondary/30 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-2xl font-serif">
                        {showResetForm ? 'Reset Password' : 'Admin Login'}
                    </CardTitle>
                    <p className="text-muted-foreground">
                        {showResetForm 
                            ? 'Answer your security question to reset password' 
                            : 'Enter your credentials to access the dashboard'}
                    </p>
                </CardHeader>
                <CardContent>
                    {!showResetForm ? (
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="admin@cravencanines.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="admin-email-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="password">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="Enter password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="admin-password-input"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full btn-primary"
                                disabled={loading}
                                data-testid="admin-login-btn"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Login
                            </Button>
                            <button
                                type="button"
                                onClick={() => setShowResetForm(true)}
                                className="w-full text-sm text-primary hover:underline"
                            >
                                Forgot password?
                            </button>
                        </form>
                    ) : (
                        <div className="space-y-4">
                            {resetStep === 1 && (
                                <>
                                    <div>
                                        <Label htmlFor="reset-email">Email</Label>
                                        <Input
                                            id="reset-email"
                                            type="email"
                                            placeholder="Enter your email"
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                        />
                                    </div>
                                    <Button 
                                        onClick={fetchSecurityQuestion}
                                        className="w-full btn-primary"
                                    >
                                        Continue
                                    </Button>
                                </>
                            )}
                            {resetStep === 2 && (
                                <>
                                    <div className="bg-secondary/50 p-3 rounded-lg">
                                        <p className="text-sm font-medium text-muted-foreground">Security Question:</p>
                                        <p className="text-foreground">{securityQuestion}</p>
                                    </div>
                                    <div>
                                        <Label htmlFor="security-answer">Your Answer</Label>
                                        <Input
                                            id="security-answer"
                                            type="text"
                                            placeholder="Enter your answer"
                                            value={securityAnswer}
                                            onChange={(e) => setSecurityAnswer(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="new-password">New Password</Label>
                                        <Input
                                            id="new-password"
                                            type="password"
                                            placeholder="Enter new password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                        />
                                    </div>
                                    <Button 
                                        onClick={handlePasswordReset}
                                        className="w-full btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                        Reset Password
                                    </Button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    setShowResetForm(false);
                                    setResetStep(1);
                                    setResetEmail('');
                                    setSecurityAnswer('');
                                    setNewPassword('');
                                }}
                                className="w-full text-sm text-muted-foreground hover:underline"
                            >
                                Back to login
                            </button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminAuth;
